/**
 * Dev Tools ▸ Black Friday — which code the band and the modal redeem, in THIS browser.
 *
 * `blackfriday2026` starts working on 20 Oct 2026; before that its redemption answers "unknown code".
 * The test codes are real promo codes (Symphony Live ▸ Promo codes) that grant the same free-usage
 * window, so the REAL path — redeem, countdown on every device, over-band — can be walked today.
 * Unlike the sim beside it, nothing here is faked: the redemption lands on the account.
 *
 * Inert without a dev-tools grant, so a key left in storage changes nothing for a real user.
 */

import { useEffect, useState } from 'react';
import { devToolsUnlocked } from './dev-tools';
import { BLACK_FRIDAY_PROMO_CODE } from './black-friday';

export type BlackFridayTestCode = 'dankov-test-48h-bfcm' | 'dankov-test-1h-bfcm';

// Shipped in the production bundle on purpose, so employees with a dev-tools grant can test on
// symphony.wix.com. The grant only hides the buttons: anyone reading the bundle can find these codes,
// which is accepted because each one is capped and expires (Symphony Live ▸ Promo codes).
export const BLACK_FRIDAY_TEST_CODES: readonly BlackFridayTestCode[] = ['dankov-test-48h-bfcm', 'dankov-test-1h-bfcm'];

const KEY = 'symphony-dev-black-friday-code';
const EVENT = 'symphony:black-friday-code';

export function getBlackFridayTestCode(): BlackFridayTestCode | null {
  if (!devToolsUnlocked() || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return BLACK_FRIDAY_TEST_CODES.includes(raw as BlackFridayTestCode) ? (raw as BlackFridayTestCode) : null;
  } catch {
    return null;
  }
}

export function setBlackFridayTestCode(code: BlackFridayTestCode | null): void {
  if (!devToolsUnlocked() || typeof window === 'undefined') return;
  try {
    if (code === null) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, code);
  } catch {
    return;
  }
  window.dispatchEvent(new Event(EVENT));
}

/** The code a press of the band's or the modal's CTA spends right now. */
export function blackFridayPromoCode(): string {
  return getBlackFridayTestCode() ?? BLACK_FRIDAY_PROMO_CODE;
}

/** Live, so the panel and an already-mounted CTA follow a switch without a reload. */
export function useBlackFridayTestCode(): BlackFridayTestCode | null {
  const [code, setCode] = useState<BlackFridayTestCode | null>(null);
  useEffect(() => {
    const read = () => setCode(getBlackFridayTestCode());
    read();
    window.addEventListener(EVENT, read);
    window.addEventListener('storage', read);
    return () => {
      window.removeEventListener(EVENT, read);
      window.removeEventListener('storage', read);
    };
  }, []);
  return code;
}
